/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useEffect, useState } from 'react';
import { VStack, Box } from 'chakra-v3';
import type { PlatformAudioComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { Button } from '../../../../../components/buttons/Index';
import { PlatformMediaErrors, PlatformFileErrors } from './ErrorMessages';
import { useNext } from '../../../hooks/useNext';
import { useErrorState } from '../../shared/useErrorState';
import { ChatBubble } from '../../shared/ChatBubble';

export const PlatformAudio: React.FC<ComponentRendererProps> = ({ node, controller, isCurrent }) => {
  const params = getTypedParams<PlatformAudioComponent>(node);
  const recording = params.recording ?? '';
  const forceReplay = params.forceReplay ?? false;

  const [hasPlayed, setHasPlayed] = useState(!forceReplay);
  const [error, setError] = useErrorState();

  // Set missing file error if recording is not provided
  useEffect(() => {
    if (!recording) {
      const missingFileError = PlatformFileErrors.getMissingFileError(recording, 'audio');
      setError(() => missingFileError);
    } else {
      setError(null);
    }
  }, [recording, setError]);

  const handlePlay = () => {
    setHasPlayed(true);
  };

  // Auto-advance when audio has played and forceReplay is not enabled
  useEffect(() => {
    if (hasPlayed && !forceReplay && isCurrent) {
      controller.next();
    }
  }, [hasPlayed, forceReplay, controller, isCurrent]);

  // Handle manual button for forceReplay scenario
  const { showManualButton, next } = useNext({
    strategy: forceReplay && hasPlayed ? 'MANUAL' : 'AUTO',
    controller,
    isCurrent,
  });

  const handleAudioError = (event: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    const error = PlatformMediaErrors.getMediaElementError(event.nativeEvent, 'audio', recording || undefined);
    setError(() => error);
  };

  return (
    <ChatBubble>
      <VStack gap="12px" align="stretch">
        {error && (
          <Text variant="body" color="error.solid">
            {error.message}
          </Text>
        )}
        {recording && (
          <Box w="100%">
            <audio src={recording} controls onPlay={handlePlay} onError={handleAudioError} style={{ width: '100%' }} />
          </Box>
        )}
        {showManualButton && (
          <Button onClick={next} variant="cta" w="100%">
            Continue
          </Button>
        )}
      </VStack>
    </ChatBubble>
  );
};
